import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';

export interface SearchResult {
  title: string;
  snippet: string;
  url: string;
  citation: string;
}

export interface ImageResult {
  title: string;
  url: string;
  thumbnailUrl: string;
  contextUrl: string;
  citation: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
  imageResults?: ImageResult[];
  screenshot?: string;
  linkPreview?: any;
  aiSummary?: string;
}

interface Props {
  data: SearchResponse;
  isDarkMode?: boolean;
}

export const SearchResultsCard: React.FC<Props> = ({ data, isDarkMode = false }) => {
  const [expandedScreenshots, setExpandedScreenshots] = useState<Set<number>>(new Set());
  const [loadingScreenshots, setLoadingScreenshots] = useState<Set<number>>(new Set([...Array(data.results.length).keys()]));
  const [failedScreenshots, setFailedScreenshots] = useState<Set<number>>(new Set());

  const bgColor = isDarkMode ? 'bg-transparent' : 'bg-white/0';
  const textColor = isDarkMode ? 'text-white' : 'text-black';
  const secondaryTextColor = isDarkMode ? 'text-white/80' : 'text-black/80';
  const tertiaryTextColor = isDarkMode ? 'text-white/60' : 'text-black/60';
  const borderColor = isDarkMode ? 'border-white/20' : 'border-black/30';
  const highlightBg = isDarkMode ? 'hover:bg-white/15' : 'hover:bg-black/5';
  const defaultBg = isDarkMode ? 'bg-white/10' : 'bg-black/2';
  const linkColor = isDarkMode ? 'text-blue-300' : 'text-blue-700';
  const headingColor = isDarkMode ? 'text-gray-200' : 'text-gray-800';

  const handleScreenshotLoad = (index: number) => {
    setLoadingScreenshots(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const handleScreenshotError = (index: number) => {
    setLoadingScreenshots(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    setFailedScreenshots(prev => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
  };

  const handleScreenshotClick = (url: string, title: string) => {
    // Use the global fullscreen ImageCarousel modal for consistency
    const images = [{ url, description: title }];
    window.dispatchEvent(new CustomEvent('openFullscreenImageModal', {
      detail: { images, initialIndex: 0 }
    }));
  };

  const handleImageClick = (index: number) => {
    // Convert imageResults to the format expected by the fullscreen modal
    const images = data.imageResults?.map(img => ({
      url: img.url,
      description: img.title
    })) || [];
    
    window.dispatchEvent(new CustomEvent('openFullscreenImageModal', {
      detail: { images, initialIndex: index }
    }));
  };

  return (
    <div className={`${bgColor} rounded-2xl ${textColor} mt-1 mb-4 overflow-hidden`} data-search-results-react>
      {/* Header */}
      <div className="mb-5">
        <h2 className={`text-xl font-semibold ${headingColor}`}>
          Search Results for "{data.query}"
        </h2>
      </div>

      {/* Search Results Grid */}
      <div className="grid gap-4 max-w-full sm:max-w-[80%] overflow-hidden roounded-none">
        {data.results.map((result, index) => {
          const isLocalhost = result.url.includes('localhost') || result.url.includes('127.0.0.1');
          const thumbnailUrl = `https://api.microlink.io?url=${encodeURIComponent(result.url)}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=480&viewport.height=300&screenshot.quality=15&screenshot.type=webp&screenshot.deviceScaleFactor=1`;
          const fullSizeUrl = `https://api.microlink.io?url=${encodeURIComponent(result.url)}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=1200&viewport.height=800&screenshot.quality=45&screenshot.type=webp`;

          const ResultWrapper = isLocalhost ? 'div' : 'a';
          const wrapperProps = isLocalhost ? {} : {
            href: result.url,
            target: '_blank',
            rel: 'noopener noreferrer'
          };

          return (
            <div key={index} className={`search-result border ${borderColor} rounded-2xl overflow-hidden`} data-url={result.url}>
              {/* Result Card */}
              <ResultWrapper
                {...wrapperProps}
                className={`block ${defaultBg} backdrop-blur-md rounded-t-2xl p-5 pb-4 transition-all duration-300 no-underline ${textColor} relative break-words cursor-pointer ${highlightBg}`}
              >
                {/* External Link Icon */}
                <ExternalLink 
                  className={`absolute top-5 right-5 w-4 h-4 ${tertiaryTextColor} opacity-60 transition-all duration-200 pointer-events-none`}
                  strokeWidth={2}
                />

                {/* Title */}
                <div className="flex items-start mb-3 mr-10">
                  <h3 className={`text-lg font-semibold m-0 leading-snug flex-1 break-words ${headingColor}`}>
                    {result.title}
                  </h3>
                </div>

                {/* Snippet */}
                <p className={`${secondaryTextColor} mb-4 leading-relaxed break-words`}>
                  {result.snippet}
                </p>

                {/* Citation */}
                <div className="flex justify-between items-center gap-3 mr-10">
                  <span className={`${tertiaryTextColor} text-xs px-2 py-1 ${defaultBg} rounded break-words`}>
                    {result.citation}
                  </span>
                </div>
              </ResultWrapper>

              {/* Screenshot Preview */}
              <div 
                className="mt-0.5 h-36 rounded-b-2xl overflow-hidden cursor-pointer bg-transparent flex items-center justify-center relative"
                onClick={() => handleScreenshotClick(fullSizeUrl, result.title)}
              >
                {/* Loading State */}
                {loadingScreenshots.has(index) && (
                  <div className={`absolute inset-0 flex items-center justify-center`}>
                    <div
                      className={`${isDarkMode ? 'border-white/30 border-t-white' : 'border-black/20 border-t-black'} h-8 w-8 rounded-full border-2 animate-spin`}
                      aria-label="Loading"
                    />
                  </div>
                )}

                {/* Screenshot Image */}
                <img
                  src={thumbnailUrl}
                  alt={`Preview of ${result.title}`}
                  className="w-full h-36 object-cover rounded-b-lg shadow-md transition-transform duration-200 hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                  onLoad={() => handleScreenshotLoad(index)}
                  onError={() => handleScreenshotError(index)}
                  style={{ display: failedScreenshots.has(index) ? 'none' : 'block' }}
                />

                {/* Fallback State */}
                {failedScreenshots.has(index) && (
                  <div className={`flex items-center justify-center ${tertiaryTextColor} text-xs rounded-b-lg`}>
                    📄 Preview unavailable
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Results Section */}
      {data.imageResults && data.imageResults.length > 0 && (
        <div className="mt-6">
          <h3 className={`text-xl font-semibold mb-4 ${headingColor}`}>
            Related Images
          </h3>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
            {data.imageResults.map((image, index) => (
              <div
                key={index}
                onClick={() => handleImageClick(index)}
                className={`rounded-xl p-0 transition-all duration-300 cursor-pointer`}
              >
                <img
                  src={image.url}
                  alt={(image as any).title || 'Image'}
                  className="w-full h-32 object-cover rounded-lg mb-2"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Screenshot Section */}
      {data.screenshot && (
        <div className="mt-6">
          <h3 className={`text-xl font-semibold mb-4 ${headingColor}`}>
            📸 Website Screenshot
          </h3>
          <div className={`rounded-xl overflow-hidden border-2 ${borderColor} shadow-2xl`}>
            <img
              src={data.screenshot}
              alt="Website Screenshot"
              className="w-full h-auto block"
            />
          </div>
        </div>
      )}

      {/* Link Preview Section */}
      {data.linkPreview && (
        <div className="mt-6">
          <h3 className={`text-xl font-semibold mb-4 ${headingColor}`}>
            🔗 Link Preview
          </h3>
          <a
            href={data.linkPreview.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`block ${defaultBg} backdrop-blur-md border ${borderColor} rounded-xl p-5 no-underline ${textColor} transition-all duration-300 ${highlightBg}`}
          >
            {data.linkPreview.image && (
              <div
                className="w-full h-52 bg-cover bg-center rounded-lg mb-4"
                style={{ backgroundImage: `url('${data.linkPreview.image}')` }}
              />
            )}
            <h4 className={`text-lg font-semibold m-0 mb-2 leading-snug ${headingColor}`}>
              {data.linkPreview.title}
            </h4>
            <p className={`${secondaryTextColor} m-0 mb-3 leading-relaxed`}>
              {data.linkPreview.description}
            </p>
            <div className={`flex items-center gap-2 text-sm ${tertiaryTextColor}`}>
              {data.linkPreview.icon ? (
                <img src={data.linkPreview.icon} alt="Favicon" className="w-4 h-4" />
              ) : (
                <span className={linkColor}>🌐</span>
              )}
              <span>{data.linkPreview.url}</span>
            </div>
          </a>
        </div>
      )}

      {/* AI Summary Section */}
      {data.aiSummary && (
        <div className="mt-6">
          <h2 className={`text-xl font-semibold mb-4 ${headingColor}`}>
            <b>🤖 AI Summary</b>
          </h2>
          <div className={`${defaultBg} backdrop-blur-md border ${borderColor} rounded-xl p-5`}>
            <p className={`${secondaryTextColor} leading-relaxed m-0 text-base`}>
              {data.aiSummary}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
